import { useState, useRef } from 'react';
import { Search, Clock, ArrowRight, Zap, Database, Shield, User, LogOut } from 'lucide-react';
import { searchHistory } from '../data/mockData';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';

const EXAMPLE_SEARCHES = [
  { label: '17-41245466-G-A', desc: 'BRCA1 frameshift variant' },
  { label: 'rs80357914', desc: 'dbSNP rsID lookup' },
  { label: 'BRCA2 c.5946delT', desc: 'HGVS notation' },
  { label: '13-32338674-T-A', desc: 'BRCA2 missense' },
];

function GenomicBackground() {
  const rows = 18;
  const cols = 28;
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none dna-bg-pulse"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((_, c) => {
          const x = (c / (cols - 1)) * 100;
          const y = (r / (rows - 1)) * 100;
          const size = (Math.sin(r * 0.7 + c * 0.5) + 1) * 1.5 + 1;
          return (
            <circle
              key={`${r}-${c}`}
              cx={`${x}%`}
              cy={`${y}%`}
              r={size}
              fill="white"
              opacity="0.06"
            />
          );
        })
      )}
    </svg>
  );
}

export default function Landing({ onSearch, onAuthOpen, onPricing }) {
  const { user, signOut } = useAuth();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const handleSearch = () => {
    const q = query.trim();
    if (q) onSearch(q);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const fillQuery = (q) => {
    setQuery(q);
    inputRef.current?.focus();
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #021f30 0%, #031829 60%, #010f18 100%)' }}
    >
      <GenomicBackground />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4">
        {/* Logo + beta */}
        <div className="flex items-center gap-2">
          <Logo size="sm" />
          <span className="text-xs px-1.5 py-0.5 rounded-full text-white/50 border border-white/20 font-mono hidden sm:inline">beta</span>
        </div>

        {/* Nav */}
        <nav className="flex items-center gap-3 sm:gap-5 text-sm text-white/60">
          <a href="#" className="hover:text-white transition-colors hidden sm:block">Docs</a>
          <button onClick={onPricing} className="hover:text-white transition-colors">Pricing</button>
          <a href="#" className="hover:text-white transition-colors hidden sm:block">About</a>

          {user ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: '#f93441' }}>
                {(user.user_metadata?.full_name || user.email || '?')[0].toUpperCase()}
              </div>
              <button onClick={signOut}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/20 text-white/60 hover:text-white transition-all text-xs">
                <LogOut size={11} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          ) : (
            <button onClick={onAuthOpen}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/20 text-white/80 hover:border-white/40 hover:text-white transition-all text-sm">
              <User size={13} />
              <span>Sign in</span>
            </button>
          )}
        </nav>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="flex flex-col items-center gap-7 w-full max-w-xl mx-auto">

          {/* Hero logo */}
          <div className="flex flex-col items-center gap-4 animate-fade-in">
            <img
              src="/haploix.png"
              alt="Haploix"
              className="w-48 sm:w-80 md:w-96 h-auto"
            />
            <p className="text-white/60 text-base sm:text-lg text-center max-w-sm leading-relaxed">
              Clinical-grade variant interpretation, for free.
            </p>
          </div>

          {/* Search box */}
          <div className="w-full animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <div
              className="relative flex items-center rounded-2xl overflow-hidden shadow-2xl"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.15)' }}
            >
              <Search className="absolute left-4 text-white/40 shrink-0" size={18} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKey}
                placeholder="e.g., 1-12345-A-T or rs123456"
                className="w-full bg-transparent text-white placeholder-white/40 pl-11 pr-3 py-4 text-base outline-none font-mono"
              />
              <button
                onClick={handleSearch}
                disabled={!query.trim()}
                className="shrink-0 m-2 flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-white transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-95"
                style={{ background: '#f93441' }}
              >
                <Zap size={14} />
                Analyze
              </button>
            </div>

            {/* Recent searches */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <div className="flex items-center gap-1 text-white/40 text-xs">
                <Clock size={11} />
                Recent:
              </div>
              {searchHistory.map((s) => (
                <button
                  key={s}
                  onClick={() => fillQuery(s)}
                  className="px-2.5 py-1 rounded-full text-xs font-mono text-white/70 border border-white/15 hover:border-white/35 hover:text-white transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Example searches */}
          <div className="w-full animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-3 text-center">
              Example searches
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              {EXAMPLE_SEARCHES.map((ex) => (
                <button
                  key={ex.label}
                  onClick={() => fillQuery(ex.label)}
                  className="group flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                >
                  <div className="min-w-0">
                    <div className="text-white font-mono text-xs truncate">{ex.label}</div>
                    <div className="text-white/45 text-xs mt-0.5 truncate">{ex.desc}</div>
                  </div>
                  <ArrowRight size={13} className="text-white/30 shrink-0 ml-1" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Feature strip */}
      <footer className="relative z-10 border-t border-white/10 py-5 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto grid grid-cols-3 gap-3 sm:gap-6">
          {[
            { icon: Database, label: '12+ Databases', desc: 'gnomAD, ClinVar, OMIM & more' },
            { icon: Zap, label: 'AI Ensemble', desc: 'AlphaMissense, SpliceAI, REVEL' },
            { icon: Shield, label: 'ACMG/AMP', desc: 'Automated classification' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex flex-col sm:flex-row items-center sm:items-start gap-2 text-center sm:text-left">
              <div className="p-1.5 rounded-lg shrink-0" style={{ background: 'rgba(249,52,65,0.15)' }}>
                <Icon size={16} style={{ color: '#f93441' }} />
              </div>
              <div>
                <div className="text-white text-xs font-semibold">{label}</div>
                <div className="text-white/40 text-xs hidden sm:block">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
}
